import React, { Suspense } from 'react';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import { useApolloClient } from '@apollo/client';
import { DocumentNode } from 'graphql';
import ProgramVideoMetadata from '@ecosystems/programs/ProgramVideoMetadata';
import { Box } from '@components/layout/Grid';
import VideoLockOverlay from './VideoLockOverlay';
import AnalyticsManager from '@lib/analytics/manager';
import { ProgramCategory, ProgramVideo } from '@gql/generated';
import { Trans } from '@lingui/react';
import { CHALLENGE_PROGRAMS } from '@pages/[lang]/challenges';

const VideoPlayer = dynamic(() => import('@organisms/video-player'), {
  ssr: false,
  // loading: () => null,
});

const Placeholder = styled.div`
  aspect-ratio: 16 / 9;
  background-color: #000;
`;

const Details = styled(Box)`
  @media (min-width: ${({ theme }) => theme.viewports.laptop}px) {
    width: 90%;
    margin: 0 auto;
  }
`;

const WeekDescription = styled.div`
  display: none;

  @media (min-width: ${({ theme }) => theme.viewports.laptop}px) {
    display: block;
    border: 2px solid #e4e4e4;
    border-radius: 3px;
    padding: 20px;

    h1,
    h2,
    h3,
    h4,
    p {
      margin-top: 0;
    }
  }
`;

// no need to keep this in state;
const completedVideos = [];

type Props = {
  programTitle: string;
  programId: string;
  weekTitle?: string;
  hasAccess: boolean;
  programQuery: DocumentNode;
  currentElement: Pick<
    ProgramVideo,
    | 'id'
    | 'title'
    | 'customTitle'
    | 'locked'
    | 'slug'
    | 'video'
    | 'customDescription'
  >;
  programProgress: number;
  weekDescription: string;
  lockOverlayText?: React.ReactNode;
  lockOverlayButtonText?: React.ReactNode;
  programCategory: ProgramCategory;
  onVideoComplete?(): void;
};

// TODO: WE SHOULD LIFT THE OnEnd/onTimeUpdate TO PROPS TO ALLOW MUTATION FLEXIBILITY
const VideoPlayerSection = ({
  programId,
  weekTitle,
  currentElement,
  hasAccess,
  programQuery,
  programProgress,
  weekDescription,
  lockOverlayText,
  lockOverlayButtonText,
  programCategory,
  onVideoComplete,
}: Props) => {
  const client = useApolloClient();

  if (!currentElement.video) {
    return (
      <Box mb={[0, 0, 0, 40]} data-testid="VideoPlayerSection">
        <Box mb="30px" style={{ position: 'relative' }}>
          {/*  */}
        </Box>
      </Box>
    );
  }

  function updateVideoProgress() {
    if (!currentElement.locked) {
      if (!completedVideos.includes(currentElement.id)) {
        completedVideos.push(currentElement.id);
        AnalyticsManager().sendProgramVideoCompletionEvent({
          weekTitle,
          programTitle: programId,
          videoSlug: currentElement?.slug,
          videoTitle: currentElement.title || currentElement?.video?.title,
        });
        onVideoComplete?.();
      }
    }
  }

  function refetchPogram() {
    // we must keep this at the end of video
    // for exercise videos, if video is marked as watched the post mood tracker will popup
    // even tho the video is not fully finished because of >= 0.8 progress update
    client
      .query({
        query: programQuery,
        variables: { slug: programId },
        fetchPolicy: 'network-only',
      })
      .then((resp) => {
        // send program completed event
        if (programProgress < 100 && resp?.data?.progress === 100) {
          AnalyticsManager().sendProgramCompletionEvent({
            title: '',
            slug: programId,
            category: programCategory,
          });
        }
      });
  }

  let _lockOverlayText = lockOverlayText,
    _lockOverlayButtonText = lockOverlayButtonText;
  if (programCategory === ProgramCategory['Courses']) {
    _lockOverlayButtonText = (
      <Trans id="courses_2021.progress_page.video_lock.button_text" />
    );
    _lockOverlayText = (
      <Trans id="courses_2021.progress_page.video_lock.text" />
    );
  } else {
    (_lockOverlayText = (
      <Trans id="programs_2021.progress_page.video_lock.text" />
    )),
      (_lockOverlayButtonText = (
        <Trans id="programs_2021.progress_page.video_lock.button_text" />
      ));
  }

  const currentVideo = currentElement.video;
  return (
    <Box mb={[0, 0, 0, 40]} data-testid="VideoPlayerSection">
      <Box mb="30px" className="relative aspect-video bg-neutral-100">
        <VideoPlayer
          className="absolute top-0 left-0 size-full"
          autoPlay={false}
          key={currentVideo.id}
          id={currentVideo.id}
          title={currentElement.title}
          video={currentVideo}
          isLimited={currentElement.locked}
          description={
            currentVideo.content?.longDescription ||
            currentVideo.content?.shortDescription
          }
          kind={currentVideo.kind}
          // QUESTION: do we really need this if we can imitate it onTimeUpdate?
          onEnd={async () => {
            // we only send progress if video is inlock (not preview)
            // this.updateProgress(currentElement.id, program);
            // await updateVideoProgress(true);
            refetchPogram();
          }}
          onTimeUpdate={(duration, current) => {
            if (!currentElement.locked) {
              if (current > 0.8 * duration) {
                updateVideoProgress();
              } else if (current === duration) {
                refetchPogram();
              }
            }
          }}
          videoSources={currentVideo.player?.assets}
          thumbnailImage={currentVideo.assets?.thumbnailUrl}
          videoEndOverlay={() => {
            let buttonUrl = `/se/${programCategory}/${programId}/get-started`;
            if (CHALLENGE_PROGRAMS.includes(programId)) {
              buttonUrl = `/se/challenges/${programId}/get-started`;
            }
            return (
              <VideoLockOverlay
                text={_lockOverlayText}
                buttonText={_lockOverlayButtonText}
                hasAccess={hasAccess}
                buttonUrl={buttonUrl}
              />
            );
          }}
        />
      </Box>
      <Details mx={[10]} mb={[0, 0, 0, 20]}>
        <ProgramVideoMetadata
          id={currentVideo.id}
          identifier={currentVideo.identifier}
          title={
            currentElement.customTitle ||
            currentElement.title ||
            currentElement.video.title
          }
          description={
            currentElement?.customDescription ||
            currentVideo.content.longDescription ||
            currentVideo?.content?.shortDescription
          }
          duration={currentVideo.meta.minutes}
          needs={currentVideo.taxonomies?.needs}
          instructors={currentVideo.taxonomies?.instructors}
          genre={currentVideo.taxonomies?.genre?.name}
          genreSlug={currentVideo.taxonomies?.genre?.slug}
          locked={currentElement.locked}
          noActions={programCategory === ProgramCategory['Courses']}
        />
        {
          // outro videos are not phase so we dont have description
          weekDescription ? (
            <WeekDescription
              dangerouslySetInnerHTML={{
                __html: weekDescription,
              }}
            />
          ) : null
        }
      </Details>
    </Box>
  );
};

export default VideoPlayerSection;
